// The page: xterm.js with the HOTTY addon, running hotty-demo compiled to
// WebAssembly: the HOTTY website (the tour and its pages), and the web
// shell, whose commands are the tools, the website and the tabbed demo. The program's output goes to the terminal; the terminal's
// input (keys, and the addon's replies and events) goes to the program.
// cmd/hotty-demo-wasm is the Go half of this bridge.
//
// No function the Go program can reach may throw: syscall/js turns a JS
// exception into a Go panic that kills it. Every host method is guarded.
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { UnicodeGraphemesAddon } from "@xterm/addon-unicode-graphemes";
import { WebglAddon } from "@xterm/addon-webgl";
import { HottyAddon } from "@neuroplastio/xterm-addon-hotty";
import * as procs from "./procs";
import { isDocs, root, routeOf } from "./route";
import { watchKeyTakers } from "./keytakers";
import * as modes from "../../../shared/sitebar/modes";
import { choose, chosen } from "../../../shared/sitebar/tui";
import * as toc from "../../../shared/siteshell/toc";

declare const Go: new () => { importObject: WebAssembly.Imports; run(i: WebAssembly.Instance): Promise<void> };
declare global {
  interface Window {
    hottyDemoHost: Record<string, unknown>;
    hottyDemo?: {
      write(b: Uint8Array): void;
      resize(cols: number, rows: number): void;
      open(app: string): void;
      visible(on: boolean): void;
      hotty(on: boolean): void;
    };
    demo: {
      term: Terminal;
      hotty: HottyAddon;
      ready: boolean;
      exited: boolean;
      // How many times the shell has shown its prompt: tests wait for it
      // to grow after a command (tests/shell.ts).
      prompts: number;
      trace?: string[];
      bench?: Record<string, unknown>;
      // What the program last put on the clipboard (OSC 52), for tests.
      clipboard?: string;
      // The program's WebAssembly memory, in bytes, for measuring.
      memory?: () => number;
      // The programs running in the demo's virtual terminals (procs.ts).
      procs: typeof procs.stats;
    };
  }
}

const status = document.getElementById("stage-status")!;
// A phone or a tablet: its finger is the pointer.
const touchScreen = matchMedia("(pointer: coarse)").matches;

// Each route has its own path under the page's root (which may have a prefix
// of its own, route.ts): the website's pages (/what, /install; the root is
// its home, the tour), the docs (/docs, /docs/install), the shell's prompt
// (/shell), and the demo's apps (/metrics). The program starts at it, its
// links point at it, and the path follows what is on screen.
// The site's own pages, with a header of their own ("" is its home), the
// docs' too.
const SITE = ["", "what", "architecture", "install", "apps"];
const onSite = (route: string) => SITE.includes(route) || isDocs(route);
// The pages of prose and the docs' are web pages too (internal/site's
// WebPages, written by cmd/pages; ../docs' cmd/site -into; infra's routes
// and pages): the reader's choice of web shows one in the program's place.
const WEB = ["what", "architecture", "install"];
const isWeb = (route: string) => WEB.includes(route) || isDocs(route);
const startRoute = routeOf(location.pathname);
// This document's web page, if it has one (data-page), and whether it is
// shown (data-view, src/mode.ts): the program starts when it shows.
const html = document.documentElement;
const webPage = html.dataset.page ?? "";
let onPage = html.dataset.view === "page";
// A page of the docs' On this page follows the reader while it shows.
const contents = toc.follow(() => onPage);
const term = new Terminal({
  // term.unicode, which the Unicode addon sets.
  allowProposedApi: true,
  fontFamily: '"IBM Plex Mono", "JetBrains Mono", "DejaVu Sans Mono", ui-monospace, monospace',
  // A phone gets about 54 columns: every tab fits (at 15px it would be 40).
  fontSize: matchMedia("(max-width: 520px)").matches ? 12 : 15,
  lineHeight: 1.15,
  cursorBlink: false,
  // The shell's output stays above its prompt, surfaces included. The
  // demo's apps run on the alternate screen, which has none.
  scrollback: 5000,
  // Key releases for programs that want them (HOTTY SPEC §10.3).
  vtExtensions: { kittyKeyboard: true },
  theme: {
    background: "#12121a",
    foreground: "#d7d7e0",
    cursor: "#9d90ff",
    selectionBackground: "#5b4bd655",
    black: "#12121a", red: "#f07a7a", green: "#7fd4a0", yellow: "#e8c872",
    blue: "#8fa8ff", magenta: "#9d90ff", cyan: "#7fd4d4", white: "#d7d7e0",
    brightBlack: "#5b6270", brightRed: "#ff9a9a", brightGreen: "#9fe4b8", brightYellow: "#f2d892",
    brightBlue: "#aec0ff", brightMagenta: "#b9afff", brightCyan: "#9fe4e4", brightWhite: "#ffffff",
  },
});
const fit = new FitAddon();
// OSC 8 links in cells, and hyperlinks in surfaces (HOTTY SPEC §9), which
// go where OSC 8 links go: this page's policy is a new tab without an opener
// for http and https (xterm.js ignores other schemes), not xterm.js's
// confirm dialog. The site's own pages, the docs' too, are the program's,
// which shows them in place: none is a hyperlink.
term.options.linkHandler = {
  activate: (_e, uri) => void window.open(uri, "_blank", "noopener,noreferrer"),
};
/** The keys the browser keeps: the addon's default (reload, zoom, full
 * screen, the developer tools, Cmd on a Mac), and Ctrl+Shift+V, the
 * browser's paste, which xterm.js would send as a key: it reaches the
 * program as a bracketed paste. The edit keys a program takes as commands
 * (SPEC §10.6), Cmd's on a Mac among them, are its own before these: the
 * storybook's copy, cut, paste, undo and redo. */
const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
function browserKeys(e: KeyboardEvent): boolean {
  if (e.key === "F5" || e.key === "F11" || e.key === "F12") return true;
  if (mac && e.metaKey) return true;
  const mod = e.ctrlKey || e.metaKey;
  const k = e.key.toLowerCase();
  if (mod && !e.altKey && k === "r") return true;
  if (mod && ["=", "+", "-", "0"].includes(e.key)) return true;
  if (e.ctrlKey && e.shiftKey && ["i", "j", "c", "v"].includes(k)) return true;
  return false;
}
// Where the storybook's pictures come from (internal/apps/storybook).
const IMAGE_HOSTS = ["https://images.unsplash.com", "https://upload.wikimedia.org"];

const hotty = new HottyAddon({
  browserKeys,
  onInvalid: (reason) => console.warn("hotty-demo: the addon dropped a hotty message:", reason),
  // The font hotty hosts ship, which build.mjs serves from the addon:
  // what the terminal's font lacks (Nerd Font icons, keys' symbols)
  // draws in it.
  fonts: "/xterm-addon-hotty/fonts/",
  // Surfaces may load images from this page's origin when they ask for them
  // (HOTTY SPEC §7.2), and from the two hosts the storybook's stories show
  // pictures from: A2UI's examples (Unsplash) and the kit's own (Wikimedia).
  // index.html's CSP allows the same.
  network: { "img-src": [location.origin, ...IMAGE_HOSTS] },
});
term.loadAddon(fit);
term.loadAddon(hotty);
// A character takes the columns the program counts: grapheme clusters at
// Unicode 15's widths (an emoji, ⌨️ with its VS16, a ZWJ sequence: two), as
// the kit's cells and native terminals measure them, not xterm.js's own
// Unicode 6, where an emoji takes one and covers the column after it.
term.loadAddon(new UnicodeGraphemesAddon());
const stage = document.getElementById("stage")!;
const chrome = document.getElementById("chrome")!;
const termEl = document.getElementById("term")!;
term.open(termEl);
useWebgl();
place();

/** Draws the cells with WebGL rather than the DOM: it draws block and box
 * characters itself, edge to edge, where the DOM renderer shows the font's
 * glyphs with the line height's gaps around them (the tour's block
 * letters). Without WebGL, or once its context is lost, the DOM renderer
 * takes over; `?renderer=dom` keeps it, to compare the two by eye, and
 * `?renderer=webgl` asks for WebGL where the page would not use it.
 *
 * A touch screen keeps the DOM renderer: on a phone's GPU the WebGL
 * renderer was seen to garble glyphs (Firefox on Android: the tour's full
 * blocks gone from its white letters, stray marks after an "s"), where
 * the DOM draws them all. A likely cause: its fragment shader reads the
 * glyph atlas at lowp precision, which a desktop's GPU ignores and a
 * phone's may not. */
function useWebgl() {
  const asked = new URLSearchParams(location.search).get("renderer");
  if (asked === "dom" || (touchScreen && asked !== "webgl")) return;
  try {
    const gl = new WebglAddon();
    gl.onContextLoss(() => gl.dispose());
    term.loadAddon(gl);
  } catch (e) {
    console.info("hotty-demo: no WebGL, drawing with the DOM renderer", e);
  }
}

// OSC 22: the pointer's shape, a CSS cursor name, as kitty and Ghostty take
// it. The program sets it over what it can click; "default" hands it back
// to xterm.js.
term.parser.registerOscHandler(22, (data) => {
  const screen = term.element?.querySelector(".xterm-screen") as HTMLElement | null;
  if (screen) screen.style.cursor = data === "default" || data === "" ? "" : data;
  return true;
});
// OSC 52: a program sets the clipboard (sheethot's yank, a copy key in the
// kit's cells). Only the clipboard (`c`, or no target, its default): a
// browser has no primary selection, and copying every selection made (`p`)
// would overwrite the clipboard. Reads (`?`) are refused: the page would
// hand the clipboard to whatever runs.
term.parser.registerOscHandler(52, (data) => {
  const [targets = "", b64 = ""] = data.split(";", 2);
  if (b64 === "?" || (targets !== "" && !targets.includes("c"))) return true;
  try {
    const text = new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
    window.demo.clipboard = text;
    navigator.clipboard?.writeText(text).catch(() => {});
  } catch (e) {
    console.warn("hotty-demo: OSC 52 dropped", e);
  }
  return true;
});
// Taps and drags on the cells and on surfaces are the addon's (its touch
// option): a tap is a click for the program, a drag scrolls.
if (touchScreen && term.textarea) term.textarea.inputMode = "none";
// A keyboard extension that takes the terminal's keys (Vimium): the page
// suggests excluding the site from it (keytakers.ts). The page's own blur
// of the terminal is no sign of one.
let blurring = false;
try {
  if (term.textarea) watchKeyTakers(term.textarea, () => term.focus(), () => blurring);
} catch (e) {
  console.warn("hotty-demo: no watch for keyboard extensions", e);
}

window.demo = { term, hotty, ready: false, exited: false, prompts: 0, procs: procs.stats };
// `?trace`: every byte the program writes, for debugging.
if (new URLSearchParams(location.search).has("trace")) window.demo.trace = [];
// `?lag=MS`: a slower terminal, for measuring. What the program writes and
// what the terminal sends it each wait MS/2, in order, so a round trip
// grows by MS.
const lag = Number(new URLSearchParams(location.search).get("lag") ?? 0) / 2;
function later(fn: () => void) {
  if (lag > 0) setTimeout(fn, lag);
  else fn();
}
const decoder = new TextDecoder();

function guard<A extends unknown[]>(name: string, fn: (...a: A) => void) {
  return (...a: A) => {
    try {
      fn(...a);
    } catch (e) {
      console.error(`hotty-demo: ${name} threw`, e);
    }
  };
}

function fail(msg: string) {
  status.textContent = `hotty-demo stopped: ${msg}`;
  status.hidden = false;
}

window.hottyDemoHost = {
  cols: term.cols,
  rows: term.rows,
  // A page of the docs opens at the heading the address names.
  app: isDocs(startRoute) ? startRoute + location.hash : startRoute,
  query: location.search,
  // The feed's benchmark result (/feed?bench), for the page and its tests.
  benched: guard("benched", (json: string) => {
    window.demo.bench = JSON.parse(json);
  }),
  base: location.origin + root,
  // The terminal's colours, which the programs in the demo's panes ask for
  // (internal/vterm's Theme).
  theme: term.options.theme,
  // The page has neuroplast.io's bar above the terminal (shared/sitebar),
  // so the site's header leaves the organisation out.
  sitebar: document.querySelector(".npio-bar") !== null,
  // The route on screen: a page of the website ("" for its home), an app,
  // or "shell" when they quit to the prompt.
  // The window chrome shows away from the site's own pages, which have
  // theirs: at the prompt, and in an app.
  // The query said where to start (?tour=3, ?tour=off) and how (?bench,
  // ?trace), read once at the start: past it, the URL is the route alone,
  // so a reload shows what is on screen.
  // Once shown, it stays: the switch in it is how a visitor turns HOTTY
  // off, wherever they go.
  // A page of the docs may name a heading (docs/install#updating), which
  // the address names too.
  opened: guard("opened", (at: string) => {
    const [route, frag] = at.split("#", 2);
    const hash = frag ? "#" + frag : "";
    if (routeOf(location.pathname) !== route || (hash && hash !== location.hash)) history.pushState(null, "", root + route + hash);
    else if (location.search) history.replaceState(null, "", root + route + hash);
    if (!onSite(route)) stage.classList.add("revealed");
    offerPage(route);
    showSlider();
  }),
  // The shell waits for a command. Counted once xterm.js has parsed what
  // came before (it parses writes later, in order), so the prompt is on
  // the screen when the count says so.
  prompt: guard("prompt", () => {
    later(() => term.write("", () => window.demo.prompts++));
  }),
  // A phone shows its keyboard only where the program takes text (the
  // agent's prompt, a comment being written); elsewhere a tap focuses the
  // terminal without one. A desktop's keyboard is always there.
  keyboard: guard("keyboard", (on: boolean) => {
    const ta = term.textarea;
    if (!touchScreen || !ta) return;
    ta.inputMode = on ? "text" : "none";
    if (!on && document.activeElement === ta) {
      blurring = true;
      ta.blur();
      blurring = false;
    }
  }),
  output: guard("output", (bytes: Uint8Array) => {
    window.demo.trace?.push(decoder.decode(bytes));
    if (lag > 0) {
      const copy = bytes.slice();
      later(() => term.write(copy));
    } else term.write(bytes);
  }),
  // The tour's frame reached the terminal's edges: the page's own window
  // chrome takes over around it. Once the reveal has ended (0.9 s on),
  // every row is drawn again: on a GPU the WebGL renderer was seen to keep
  // a row of the frame before, though the buffer had the new one.
  reveal: guard("reveal", () => {
    stage.classList.add("revealed");
    showSlider();
    setTimeout(() => term.refresh(0, term.rows - 1), 1200);
  }),
  ready: guard("ready", () => {
    status.hidden = true;
    window.demo.ready = true;
    // The program starts with hotty on; off, if the switch said so first.
    if (!hottyOn) window.hottyDemo?.hotty(false);
    resize();
    term.focus();
  }),
  failed: guard("failed", (msg: string) => fail(msg)),
  // A program in a virtual terminal of the demo's (internal/vterm): its
  // own module, in a worker. Not guarded: the Go side recovers.
  spawn: procs.spawn,
  exited: guard("exited", () => {
    window.demo.exited = true;
    fail("the program exited. Reload the page to start it again.");
  }),
};

const encoder = new TextEncoder();
term.onData((data) =>
  later(() => {
    try {
      window.hottyDemo?.write(encoder.encode(data));
    } catch (e) {
      console.warn("hotty-demo: input dropped", e);
    }
  }),
);

/** Fits the terminal into the stage below the chrome's room, then shrinks
 * its box to whole cells and centres it, the chrome over it at its width. */
function place() {
  const room = { w: stage.clientWidth, h: stage.clientHeight - 30 };
  Object.assign(termEl.style, { width: `${room.w}px`, height: `${room.h}px` });
  fit.fit();
  const screen = term.element?.querySelector(".xterm-screen") as HTMLElement | null;
  const w = screen?.offsetWidth || room.w;
  const h = screen?.offsetHeight || room.h;
  const left = `${Math.floor((room.w - w) / 2)}px`;
  Object.assign(termEl.style, { width: `${w}px`, height: `${h}px`, left });
  Object.assign(chrome.style, { width: `${w}px`, left });
}

function resize() {
  try {
    place();
    window.hottyDemo?.resize(term.cols, term.rows);
  } catch (e) {
    console.warn("hotty-demo: resize dropped", e);
  }
}
new ResizeObserver(resize).observe(stage);
// Back and forward: the path's route is shown again; on the web page, a
// page the program went to is another document.
addEventListener("popstate", () => {
  const route = routeOf(location.pathname);
  if (onPage && route !== webPage) location.reload();
  else window.hottyDemo?.open(isDocs(route) ? route + location.hash : route);
});
// A hidden page runs its timers about once a minute, and xterm.js parses
// what it is sent on them: the demo sends nothing while the page is hidden
// (internal/demo's VisibleMsg), so that nothing piles up to play at once
// when it shows again.
document.addEventListener("visibilitychange", () => window.hottyDemo?.visible(!document.hidden && !onPage));
// The window's close: from an app, to the demo apps' picker; from the
// prompt, to the site's home, whatever runs there.
document.getElementById("close")!.addEventListener("click", () => {
  window.hottyDemo?.open(onSite(routeOf(location.pathname)) || routeOf(location.pathname) === "shell" ? "" : "apps");
  term.focus();
});
// hotty on or off, from the chrome's switch or the bar's slider (web |
// hotty | tui, shared/sitebar's modes.ts), which say the same: off, the
// program is told the terminal is no host (Shell.SetHotty), and the page
// hides what surfaces the scrollback keeps. The choice is the reader's
// across neuroplast.io's sites (the bar's tui.ts, a cookie: page, hotty or
// text), and the docs' pages read it too: a reader who chose tui gets the
// program in cells on every page but the tour's, which starts with hotty
// on, and the docs in cells. Web is a choice where the page is a web page
// too (WEB) and in the docs: there the reader who chose it gets the page,
// and elsewhere hotty. The tour keeps the slider hidden until it has
// revealed the terminal's chrome.
const hottySwitch = document.getElementById("hotty")!;
let hottyOn = true;
function setHotty(on: boolean, keep: boolean) {
  if (keep) choose(on ? "hotty" : "text");
  if (on !== hottyOn) {
    hottyOn = on;
    stage.classList.toggle("hotty-off", !on);
    try {
      window.hottyDemo?.hotty(on);
    } catch (e) {
      console.warn("hotty-demo: the switch dropped", e);
    }
  }
  hottySwitch.setAttribute("aria-checked", String(on));
  hottySwitch.title = on ? "Turn hotty off: the same program, in cells" : "Turn hotty on";
  modes.check(onPage ? "page" : on ? "hotty" : "text");
  showSlider();
}
/** The slider shows the mode once the page may: anywhere but the tour
 * before its reveal. */
function showSlider() {
  if (onPage) html.dataset.switch = "page";
  else if (routeOf(location.pathname) !== "" || stage.classList.contains("revealed"))
    html.dataset.switch = hottyOn ? "hotty" : "text";
  else delete html.dataset.switch;
}
/** Web is a choice on the route's page if it is a web page too. */
function offerPage(route: string) {
  if (isWeb(route)) modes.enable("page");
  else modes.disable("page", "This page is the program's alone, in the terminal: the pages of prose and the docs are web pages too");
}

/** The web page in the program's place: this document's, or, where the
 * program has gone to another since, that page's document. The program
 * waits, sending nothing, unseen. */
function showPage() {
  choose("page");
  if (routeOf(location.pathname) !== webPage) {
    location.reload();
    return;
  }
  onPage = true;
  html.dataset.view = "page";
  stage.inert = true;
  window.hottyDemo?.visible(false);
  modes.check("page");
  showSlider();
  contents?.queue();
}
/** The program in the web page's place, started if it was not. */
function showProgram() {
  if (!onPage) return;
  onPage = false;
  delete html.dataset.view;
  stage.inert = false;
  start();
  window.hottyDemo?.visible(!document.hidden);
  resize();
}
offerPage(startRoute);
modes.slider((mode, pointer) => {
  if (mode === "page") {
    showPage();
    return;
  }
  showProgram();
  setHotty(mode === "hotty", true);
  if (pointer) term.focus();
});
hottySwitch.addEventListener("click", () => {
  setHotty(!hottyOn, true);
  term.focus();
});
setHotty(startRoute === "" || chosen() !== "text", false);
term.onTitleChange((t) => (document.title = t));

// The web page's copy buttons, on its shell blocks: the block, on the
// clipboard, as the program puts it there (internal/site's pageEvent, with
// its labels).
for (const b of document.querySelectorAll<HTMLButtonElement>("#page .code.sh button")) {
  b.addEventListener("click", () => {
    const text = b.parentElement?.querySelector("pre")?.textContent ?? "";
    window.demo.clipboard = text;
    navigator.clipboard?.writeText(text).catch(() => {});
    b.textContent = "copied ✓";
    setTimeout(() => (b.textContent = "copy"), 2000);
  });
}

// The program, which starts at once unless the web page is shown; there,
// pointing at a terminal's mode starts its download, the most of the wait.
let wasm: Promise<Response> | undefined;
let started = false;
function warm() {
  wasm ??= fetch(root + "hotty-demo.wasm");
}
function start() {
  if (started) return;
  started = true;
  warm();
  const go = new Go();
  WebAssembly.instantiateStreaming(wasm!, go.importObject)
    .then((r) => {
      // Go exports its memory as mem, TinyGo as memory.
      const mem = (r.instance.exports.mem ?? r.instance.exports.memory) as WebAssembly.Memory | undefined;
      if (mem) window.demo.memory = () => mem.buffer.byteLength;
      return go.run(r.instance);
    })
    .catch((e) => {
      console.error("hotty-demo: WebAssembly failed", e);
      fail(String(e));
    });
}
if (onPage) {
  stage.inert = true;
  for (const b of document.querySelectorAll<HTMLElement>('.npio-modes [data-mode]:not([data-mode="page"])'))
    for (const e of ["pointerenter", "focus", "touchstart"]) b.addEventListener(e, warm, { once: true, passive: true });
} else start();
